import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import {
    getAttachmentContentId,
    getAttachmentFilename,
    getAttachmentId,
    getMessageBodyHtml,
    getMessageId,
    htmlReferencesContentId,
    isEmbeddedInBodyAttachment,
    parseFilenameFromDisposition,
    rewriteCidReferences,
    saveBlobAsFile,
    splitMessageAttachments,
} from '../../../helpers';

const asBlob = (result) => {
    if (result?.blob instanceof Blob) {
        return result.blob;
    }

    return new Blob([result?.blob], {
        type: result?.contentType || 'application/octet-stream',
    });
};

const useMailboxAttachments = ({
    t,
    message,
}) => {
    const service = useMailboxService();
    const [downloadingId, setDownloadingId] = useState(null);
    const [previewUrls, setPreviewUrls] = useState({});
    const [cidToUrl, setCidToUrl] = useState({});
    const html = getMessageBodyHtml(message);
    const {
        inline,
        regular,
    } = useMemo(
        () => splitMessageAttachments(message),
        [message]
    );
    const messageId = getMessageId(message);

    useEffect(() => {
        const cidAttachments = inline.filter((attachment) => {
            if (isEmbeddedInBodyAttachment(attachment) || !getAttachmentId(attachment)) {
                return false;
            }

            const contentId = getAttachmentContentId(attachment);

            return contentId && htmlReferencesContentId(html, contentId);
        });

        if (!cidAttachments.length) {
            setPreviewUrls({});
            setCidToUrl({});
            return undefined;
        }

        let cancelled = false;
        const createdUrls = [];

        const loadCidPreviews = async () => {
            const nextPreviewUrls = {};
            const nextCidToUrl = {};

            await Promise.all(cidAttachments.map(async (attachment) => {
                const attachmentId = getAttachmentId(attachment);
                const contentId = getAttachmentContentId(attachment);

                try {
                    const result = await service.fetchAttachment(attachmentId);
                    const objectUrl = URL.createObjectURL(asBlob(result));
                    createdUrls.push(objectUrl);
                    nextPreviewUrls[attachmentId] = objectUrl;
                    nextCidToUrl[contentId] = objectUrl;
                } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error(error);
                    }
                }
            }));

            if (!cancelled) {
                setPreviewUrls(nextPreviewUrls);
                setCidToUrl(nextCidToUrl);
            }
        };

        loadCidPreviews();

        return () => {
            cancelled = true;
            createdUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [html, inline, messageId, service]);

    const rewrittenHtml = rewriteCidReferences(html, cidToUrl);

    const downloadAttachment = async (attachment) => {
        const id = getAttachmentId(attachment);

        if (!id || downloadingId) {
            return;
        }

        setDownloadingId(id);

        try {
            const result = await service.fetchAttachment(id);
            const filename = parseFilenameFromDisposition(
                result.contentDisposition,
                getAttachmentFilename(attachment)
            );

            saveBlobAsFile(asBlob(result), filename);
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while downloading the attachment.'), {
                type: 'error',
            });
        } finally {
            setDownloadingId(null);
        }
    };

    return {
        regularAttachments: regular,
        rewrittenHtml,
        previewUrls,
        downloadAttachment,
        downloadingId,
    };
};

export default useMailboxAttachments;
